import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

/**
 * Ticket #91 — défilement temporel fluide, commun au Planning et à la vue
 * calendaire des imputations.
 *
 * Reprend la fenêtre glissante du Planning (ticket #9) : la fenêtre CHARGÉE
 * fait cinq écrans (deux de marge de chaque côté) et glisse d'un écran quand la
 * vue approche d'un bord, `scrollLeft` étant compensé pour que les mêmes dates
 * restent à leur place. Trois corrections par rapport à la version d'origine :
 *
 *  1. Après un glissement, l'état `scrollLeft` est resynchronisé dans le même
 *     layout effect que la compensation. Sans cela la virtualisation calculait
 *     les jours visibles avec l'ancienne position et la nouvelle `period.from`
 *     (décalage d'un écran) : une image de cellules vides jusqu'au scroll
 *     suivant — le clignotement signalé.
 *  2. Les flèches et le clavier n'utilisent plus `scrollTo({ behavior: 'smooth' })`,
 *     que la compensation d'un glissement interrompait (à-coups). L'animation
 *     est pilotée ici, image par image, vers une DATE cible et non vers une
 *     position en pixels : si la fenêtre glisse en cours de route, la cible est
 *     simplement recalculée dans le nouveau repère.
 *  3. Une touche ← / → maintenue fait défiler en continu à vitesse constante
 *     (au lieu de relancer une animation à chaque répétition clavier).
 *
 * L'état `scrollLeft` / `scrollTop` n'est mis à jour qu'une fois par image
 * (`requestAnimationFrame`) : deux `setState` par événement `scroll` faisaient
 * re-rendre toute la grille plusieurs fois par image.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Cinq écrans : deux de marge de chaque côté. Trois ne suffisent pas — le seuil
// de glissement et le seuil opposé se recouvrent et la vue oscille (#9).
export const WINDOW_FACTOR = 5;
// Glissement à 0,75 écran du bord : plus large qu'un coup de molette (jamais de
// butée sur le bord, qui rognerait le mouvement), assez étroit pour que le
// glissement ne replace jamais la vue dans le seuil opposé (#9).
const EDGE_THRESHOLD_RATIO = 0.75;
// Constante de temps de l'animation des flèches (atteint ~95 % en 3×).
const EASE_MS = 70;
// Vitesse du défilement continu, touche maintenue : en écrans par seconde.
const HOLD_SCREENS_PER_SECOND = 1.2;
// Déplacement maximal par image, en jours. Doit rester sous la marge de rendu
// des vues (BUFFER_DAYS = 3) : au-delà, une image peut être peinte avant que
// les colonnes nouvellement visibles n'existent (clics rapides cumulés).
const MAX_DAYS_PER_FRAME = 2;

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function diffDays(a, b) {
  return Math.round((a.getTime() - b.getTime()) / MS_PER_DAY);
}

/** Numéro de jour absolu d'une date locale 00:00 (insensible à l'heure d'été). */
function dayNum(d) {
  return Math.round(d.getTime() / MS_PER_DAY);
}

/** Fenêtre chargée autour d'une ancre = premier jour VISIBLE (aligné à gauche). */
export function buildWindow(anchor, visibleDays) {
  const before = Math.floor((WINDOW_FACTOR - 1) / 2);
  return {
    from: addDays(anchor, -visibleDays * before),
    to: addDays(anchor, visibleDays * (WINDOW_FACTOR - before) - 1),
  };
}

function isTypingTarget(t) {
  return Boolean(
    t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable),
  );
}

/**
 * @param {object} opts
 * @param {string} opts.zoom clé du zoom courant (un changement recale la fenêtre)
 * @param {number} opts.visibleDays jours affichés à l'écran (= taille d'un glissement)
 * @param {number} opts.shiftDays pas des flèches ← / →
 * @param {(available: number) => number} opts.computeDayWidth largeur d'un jour
 *   selon la largeur disponible (viewport − colonne gauche)
 * @param {number} opts.leftColWidth largeur de la colonne sticky de gauche
 * @param {Date} opts.initialAnchor premier jour visible à l'ouverture
 * @param {(firstVisible: Date) => Date} [opts.snapZoomAnchor] recalage du premier
 *   jour visible au changement de zoom
 * @param {{current: boolean}} [opts.busyRef] geste en cours → pas de glissement
 * @param {(e: Event) => void} [opts.onScroll] appelé à chaque événement scroll
 */
export function useSlidingTimeline({
  zoom,
  visibleDays,
  shiftDays,
  computeDayWidth,
  leftColWidth,
  initialAnchor,
  snapZoomAnchor,
  busyRef,
  onScroll: onScrollExtra,
}) {
  const containerRef = useRef(null);
  const [period, setPeriod] = useState(() => buildWindow(initialAnchor, visibleDays));
  const [viewport, setViewport] = useState({ width: 1200, height: 600 });
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  // Jour à aligner sur le bord gauche (ouverture, « Aujourd'hui », zoom).
  // Réappliqué tant que l'utilisateur n'a pas fait défiler : la largeur d'un
  // jour bouge encore après le premier rendu (barre de défilement verticale).
  const pendingAnchorRef = useRef(initialAnchor);
  const viewportMeasuredRef = useRef(false);
  const slidingRef = useRef(false);
  const pendingScrollAdjustRef = useRef(null);
  // Premier jour visible (jours absolus, fractionnaire), relevé à chaque scroll.
  // Au changement de zoom, on ne peut PAS relire `scrollLeft` : la grille vient
  // d'être re-rendue avec la nouvelle largeur de jour et, plus étroite, le
  // navigateur a déjà borné la position — la vue reculait de plusieurs semaines.
  const leftDayRef = useRef(null);

  const totalDays = diffDays(period.to, period.from) + 1;
  const dayWidth = computeDayWidth(Math.max(0, viewport.width - leftColWidth));

  // Valeurs courantes pour les handlers mémoïsés sans dépendances.
  const geomRef = useRef({ dayWidth, visibleDays, shiftDays });
  geomRef.current = { dayWidth, visibleDays, shiftDays };
  const periodRef = useRef(period);
  periodRef.current = period;
  const extraRef = useRef({ onScrollExtra, snapZoomAnchor, busyRef });
  extraRef.current = { onScrollExtra, snapZoomAnchor, busyRef };

  // ---------- Viewport ----------
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        viewportMeasuredRef.current = true;
        setViewport({ width: entry.contentRect.width, height: entry.contentRect.height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---------- État de scroll, une mise à jour par image ----------
  const scrollRafRef = useRef(0);
  const syncScrollState = useCallback(() => {
    if (scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = 0;
      const el = containerRef.current;
      if (!el) return;
      setScrollLeft(el.scrollLeft);
      setScrollTop(el.scrollTop);
    });
  }, []);

  // ---------- Animation des flèches / du clavier ----------
  // `target` = premier jour visible visé, en jours absolus (fractionnaire
  // pendant un défilement continu). `heldDir` ≠ 0 tant qu'une touche est tenue.
  const animRef = useRef({ raf: 0, target: null, heldDir: 0, lastT: 0 });

  const currentLeftDays = useCallback(() => {
    const el = containerRef.current;
    const { dayWidth: dw } = geomRef.current;
    return dayNum(periodRef.current.from) + (el && dw > 0 ? el.scrollLeft / dw : 0);
  }, []);

  const cancelAnimation = useCallback(() => {
    const a = animRef.current;
    if (a.raf) cancelAnimationFrame(a.raf);
    a.raf = 0;
    a.heldDir = 0;
    a.target = null;
    a.lastT = 0;
  }, []);

  const stepRef = useRef(null);
  stepRef.current = (now) => {
    const a = animRef.current;
    const el = containerRef.current;
    const { dayWidth: dw, visibleDays: vd } = geomRef.current;
    if (!el || dw <= 0 || a.target == null) {
      a.raf = 0;
      return;
    }
    const dt = a.lastT ? Math.min(64, now - a.lastT) : 16;
    a.lastT = now;
    if (a.heldDir) a.target += (a.heldDir * HOLD_SCREENS_PER_SECOND * vd * dt) / 1000;
    const targetPx = (a.target - dayNum(periodRef.current.from)) * dw;
    const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    const reachable = Math.max(0, Math.min(maxLeft, targetPx));
    const diff = reachable - el.scrollLeft;
    // Arrivé (ou cible hors d'atteinte) : on s'arrête — sauf si un glissement
    // de fenêtre est en attente, qui rendra la cible de nouveau atteignable.
    if (!a.heldDir && Math.abs(diff) < 0.5 && !slidingRef.current) {
      if (diff !== 0) el.scrollLeft = reachable;
      a.raf = 0;
      a.target = null;
      a.lastT = 0;
      return;
    }
    let move = diff * (1 - Math.exp(-dt / EASE_MS));
    if (Math.abs(move) < 1 && diff !== 0) move = Math.sign(diff) * Math.min(1, Math.abs(diff));
    const maxMove = MAX_DAYS_PER_FRAME * dw;
    if (Math.abs(move) > maxMove) move = Math.sign(move) * maxMove;
    if (move !== 0) {
      el.scrollLeft += move;
      // Rendu dans la même image que le déplacement : la virtualisation voit
      // la nouvelle position avant que le navigateur ne peigne.
      flushSync(() => {
        setScrollLeft(el.scrollLeft);
        setScrollTop(el.scrollTop);
      });
    }
    a.raf = requestAnimationFrame((t) => stepRef.current(t));
  };

  const startAnimation = useCallback(() => {
    const a = animRef.current;
    if (!a.raf) {
      a.lastT = 0;
      a.raf = requestAnimationFrame((t) => stepRef.current(t));
    }
  }, []);

  // Tout geste de défilement de l'utilisateur (molette, glissé, barre de
  // défilement) fige l'ancrage automatique : c'est lui qui décide.
  const releaseAnchor = useCallback(() => {
    pendingAnchorRef.current = null;
  }, []);
  const onUserScrollIntent = useCallback(() => {
    releaseAnchor();
    cancelAnimation();
  }, [releaseAnchor, cancelAnimation]);

  /** Flèche ← / → : un pas de `shiftDays` ; `repeat` = touche maintenue. */
  const navigate = useCallback(
    (dir, { repeat = false } = {}) => {
      releaseAnchor();
      const a = animRef.current;
      if (repeat) {
        if (!a.heldDir && a.target == null) a.target = currentLeftDays();
        a.heldDir = dir;
        startAnimation();
        return;
      }
      const cur = currentLeftDays();
      // Pas successifs dans le même sens : ils s'additionnent à la cible en
      // cours au lieu de repartir de la position (qui est en retard sur elle).
      const base =
        a.target != null && Math.sign(a.target - cur) === dir ? a.target : Math.round(cur);
      a.heldDir = 0;
      a.target = base + dir * geomRef.current.shiftDays;
      startAnimation();
    },
    [releaseAnchor, currentLeftDays, startAnimation],
  );

  /** Fin d'appui : on termine sur un jour entier, dans le sens du défilement. */
  const releaseHold = useCallback(() => {
    const a = animRef.current;
    if (!a.heldDir) return;
    a.target = a.heldDir > 0 ? Math.ceil(a.target) : Math.floor(a.target);
    a.heldDir = 0;
    startAnimation();
  }, [startAnimation]);

  /**
   * Aligne `date` sur le bord gauche. Si elle est déjà dans la fenêtre chargée
   * (et qu'un écran complet tient après elle), on y va en défilant ; sinon on
   * reconstruit la fenêtre autour d'elle (saut).
   */
  const anchorTo = useCallback(
    (date, { smooth = true } = {}) => {
      const p = periodRef.current;
      const { visibleDays: vd } = geomRef.current;
      const idx = diffDays(date, p.from);
      const fitsInWindow = idx >= 0 && idx + vd <= diffDays(p.to, p.from) + 1;
      if (smooth && fitsInWindow && viewportMeasuredRef.current) {
        releaseAnchor();
        const a = animRef.current;
        a.heldDir = 0;
        a.target = dayNum(date);
        startAnimation();
        return;
      }
      cancelAnimation();
      pendingAnchorRef.current = date;
      setPeriod(buildWindow(date, vd));
    },
    [releaseAnchor, startAnimation, cancelAnimation],
  );

  // ---------- Glissement de fenêtre ----------
  const onScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      syncScrollState();
      if (geomRef.current.dayWidth > 0) {
        leftDayRef.current = dayNum(periodRef.current.from) + el.scrollLeft / geomRef.current.dayWidth;
      }
      extraRef.current.onScrollExtra?.(e);
      const { dayWidth: dw, visibleDays: chunk } = geomRef.current;
      // Désarmement de l'ancrage : dès que la position observée s'écarte de
      // celle que nous imposons, c'est l'utilisateur qui a fait défiler (un
      // glissé sur la barre native n'émet ni `wheel` ni `pointerdown`).
      if (pendingAnchorRef.current) {
        const idx = diffDays(pendingAnchorRef.current, periodRef.current.from);
        const target = Math.max(0, idx * dw);
        if (Math.abs(el.scrollLeft - target) > 2) pendingAnchorRef.current = null;
      }
      if (slidingRef.current || extraRef.current.busyRef?.current) return;
      // Pas de débordement horizontal → on serait en permanence « au bord ».
      if (el.scrollWidth <= el.clientWidth + 4) return;
      const delta = chunk * dw;
      const threshold = el.clientWidth * EDGE_THRESHOLD_RATIO;
      const nearRight = el.scrollLeft + el.clientWidth >= el.scrollWidth - threshold;
      const nearLeft = el.scrollLeft <= threshold;
      if (nearRight) {
        slidingRef.current = true;
        pendingScrollAdjustRef.current = -delta;
        setPeriod((p) => ({ from: addDays(p.from, chunk), to: addDays(p.to, chunk) }));
      } else if (nearLeft) {
        slidingRef.current = true;
        pendingScrollAdjustRef.current = delta;
        setPeriod((p) => ({ from: addDays(p.from, -chunk), to: addDays(p.to, -chunk) }));
      }
    },
    [syncScrollState],
  );

  // Réapplique l'ancre ou compense un glissement AVANT que le navigateur ne
  // peigne, puis resynchronise l'état de scroll dans le même passage (fix du
  // flash de cellules vides, cf. en-tête). Gère aussi le changement de zoom.
  const committedRef = useRef(null); // { zoom, dayWidth } du rendu précédent
  useLayoutEffect(() => {
    const el = containerRef.current;
    const prev = committedRef.current;
    committedRef.current = { zoom, dayWidth };
    if (!el) {
      slidingRef.current = false;
      return;
    }
    if (prev && prev.zoom !== zoom) {
      // Changement de zoom : on repart du premier jour VISIBLE relevé avant le
      // rendu (cf. `leftDayRef`).
      cancelAnimation();
      const from = periodRef.current.from;
      const leftDay =
        leftDayRef.current ?? dayNum(from) + (prev.dayWidth > 0 ? el.scrollLeft / prev.dayWidth : 0);
      const firstVisible = addDays(from, Math.round(leftDay - dayNum(from)));
      const snap = extraRef.current.snapZoomAnchor;
      const anchor = snap ? snap(firstVisible) : firstVisible;
      pendingAnchorRef.current = anchor;
      pendingScrollAdjustRef.current = null;
      slidingRef.current = false;
      setPeriod(buildWindow(anchor, visibleDays));
      return;
    }
    if (pendingAnchorRef.current && viewportMeasuredRef.current) {
      const idx = diffDays(pendingAnchorRef.current, period.from);
      const target = Math.max(0, idx * dayWidth);
      if (Math.abs(el.scrollLeft - target) > 0.5) el.scrollLeft = target;
      pendingScrollAdjustRef.current = null;
    } else if (pendingScrollAdjustRef.current != null) {
      el.scrollLeft += pendingScrollAdjustRef.current;
      pendingScrollAdjustRef.current = null;
    }
    slidingRef.current = false;
    if (dayWidth > 0) leftDayRef.current = dayNum(period.from) + el.scrollLeft / dayWidth;
    setScrollLeft(el.scrollLeft);
    setScrollTop(el.scrollTop);
  }, [period.from, period.to, dayWidth, zoom, visibleDays, cancelAnimation]);

  // ---------- Clavier ← / → ----------
  // Ignoré si le focus est dans un champ de saisie, ou si un modificateur est
  // pressé (Cmd+← = page précédente du navigateur).
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (isTypingTarget(e.target)) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      navigate(e.key === 'ArrowLeft' ? -1 : 1, { repeat: e.repeat });
    };
    const onKeyUp = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') releaseHold();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', releaseHold);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', releaseHold);
    };
  }, [navigate, releaseHold]);

  useEffect(
    () => () => {
      cancelAnimation();
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    },
    [cancelAnimation],
  );

  return {
    containerRef,
    period,
    totalDays,
    dayWidth,
    viewport,
    scrollLeft,
    scrollTop,
    navigate,
    anchorTo,
    releaseAnchor,
    /** À étaler sur le conteneur scrollable. */
    containerProps: {
      onScroll,
      onWheel: onUserScrollIntent,
      onPointerDown: onUserScrollIntent,
      onTouchStart: onUserScrollIntent,
    },
  };
}
